import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useDispatch, useSelector} from 'react-redux';
import './CrystalsExchangeModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import NotEnoughCrystalsModal from '../../pages/ShopPage/NotEnoughCrystalsModal/NotEnoughCrystalsModal';
import {getFormattedPrice} from '../../../utils/price/getFormattedPrice';
import {PRIMARY_COLOR} from '../../../styles/constants';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';
import {MINUS_SIGN} from '../../../constants/text';
import {CRYSTALS_TO_NUMENS_RATE, CRYSTALS_TO_NUMENS_RATE_PREMIUM} from '../../../constants/crystalsExchange';
import {updatePlayerRewards} from '../../../redux/actions';
import {API_BASE} from '../../../utils/apiConfig';
import BuyPremiumButton from '../../elements/BuyPremiumButton/BuyPremiumButton';

const PAGE_ID = 'CrystalsExchangeModal';
const MAX_QUANTITY = 99999;
const JUST_EXCHANGED_DURATION_MS = 2000;
// Открытие по клику на UserHeader (без привязки к конкретной покупке) —
// дефолтное количество кристаллов на обмен
const DEFAULT_QUANTITY = 100;

/**
 * Дата окончания премиума: если осталось меньше суток — с точностью до
 * минуты, иначе достаточно точности до дня.
 */
const formatPremiumExpiresAt = (premiumExpiresAt) => {
  const expires = new Date(premiumExpiresAt);
  const isLessThanDayLeft = expires.getTime() - Date.now() < 24 * 60 * 60 * 1000;
  if (isLessThanDayLeft) {
    return expires.toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }
  return expires.toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit', year: 'numeric'});
};

/**
 * Обмен кристаллов на нумены (курс CRYSTALS_TO_NUMENS_RATE, только в одну
 * сторону — обратной конвертации нет). Открывается по клику на UserHeader,
 * а также из магазина при попытке купить сундук за нумены, которых не
 * хватает (missingNumensAmount — сколько нуменов не хватает на покупку;
 * поле количества предзаполняется кристаллами, которых хватит на разницу).
 * При нехватке кристаллов на сам обмен — переиспользуем NotEnoughCrystalsModal
 * (та же модалка, что и в магазине).
 */
function CrystalsExchangeModal({onClose, onGoToCrystals, missingNumensAmount}) {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.private.auth);
  // Действующий премиум дает улучшенный курс (сервер проверяет так же на
  // момент обмена); статус вычисляется на каждом рендере из даты в профиле
  const isPremiumActive = Boolean(auth.premiumExpiresAt)
    && new Date(auth.premiumExpiresAt).getTime() > Date.now();
  const exchangeRate = isPremiumActive ? CRYSTALS_TO_NUMENS_RATE_PREMIUM : CRYSTALS_TO_NUMENS_RATE;

  const [visible, setVisible] = useState(false);
  const [quantity, setQuantity] = useState(() => (
    missingNumensAmount > 0
      ? String(Math.ceil(missingNumensAmount / exchangeRate))
      : String(DEFAULT_QUANTITY)
  ));
  const [exchanging, setExchanging] = useState(false);
  const [justExchanged, setJustExchanged] = useState(false);
  const [notEnoughCrystalsAmount, setNotEnoughCrystalsAmount] = useState(null);
  const justExchangedTimerRef = useRef(null);

  useEffect(() => {
    const showTimer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(showTimer);
  }, []);

  useEffect(() => () => clearTimeout(justExchangedTimerRef.current), []);

  const handleClose = () => {
    if (exchanging) return;
    setVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      handleClose();
    }
  };

  const handleGoToCrystals = () => {
    setVisible(false);
    setTimeout(() => {
      onGoToCrystals?.();
      onClose?.();
    }, 300);
  };

  const parsedQuantity = parseInt(quantity, 10);
  const isQuantityValid = Number.isInteger(parsedQuantity) && parsedQuantity >= 1 && parsedQuantity <= MAX_QUANTITY;
  const numensResult = isQuantityValid ? parsedQuantity * exchangeRate : 0;

  const handleQuantityStep = (delta) => {
    if (exchanging) return;
    const current = parseInt(quantity, 10);
    const base = Number.isInteger(current) ? current : 1;
    const next = Math.min(MAX_QUANTITY, Math.max(1, base + delta));
    setQuantity(String(next));
  };

  const handleExchangeClick = async () => {
    if (!isQuantityValid || exchanging) return;

    if (parsedQuantity > (auth.crystals || 0)) {
      setNotEnoughCrystalsAmount(parsedQuantity - (auth.crystals || 0));
      return;
    }

    setExchanging(true);
    try {
      const response = await fetch(`${API_BASE}/exchange-crystals`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({crystalsAmount: parsedQuantity}),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Не удалось обменять кристаллы');
      }

      const data = await response.json();
      dispatch(updatePlayerRewards(data.numensGained, undefined, false, -parsedQuantity));
      MENU_SOUND_MANAGER.playCoins(data.numensGained);
      setJustExchanged(true);
      clearTimeout(justExchangedTimerRef.current);
      justExchangedTimerRef.current = setTimeout(() => setJustExchanged(false), JUST_EXCHANGED_DURATION_MS);
    } catch (e) {
      console.error('Exchange crystals error', e);
      alert(e.message || 'Не удалось обменять кристаллы');
    } finally {
      setExchanging(false);
    }
  };

  // Портал в body: модалка открывается в том числе из UserHeader, вложенного
  // в хедеры страниц со своим stacking-контекстом (например, .shop-header с
  // z-index 10) — без портала ее z-index 1050 зажимался бы контекстом предка,
  // и боковые табы магазина рисовались бы поверх модалки
  return createPortal(
    <div
      className={`crystals-exchange-overlay ${visible ? 'crystals-exchange-overlay_visible' : ''}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className={`crystals-exchange-modal ${visible ? 'crystals-exchange-modal_visible' : ''}`}>
        <img className="crystals-exchange-icon" src="/img/icons/crystal.png" alt="Кристаллы"/>

        <h2 className="crystals-exchange-title">Обмен кристаллов <br/> на нумены</h2>

        {missingNumensAmount > 0 && (
          <p className="crystals-exchange-missing-note">
            Для покупки не хватает{' '}
            <span className="crystals-exchange-missing-value text-coins">
              {getFormattedPrice(missingNumensAmount)}
              <img className="crystals-exchange-rate-icon" src="/img/icons/numens.png" alt="Нумены"/>
            </span>
          </p>
        )}

        <p className="crystals-exchange-rate">
          <span className={'text-crystals'}>1</span>
          <img className="crystals-exchange-rate-icon" src="/img/icons/crystal.png" alt="Кристалл"/>
          =
          <span className={'text-coins'}>{getFormattedPrice(exchangeRate)}</span>
          <img className="crystals-exchange-rate-icon" src="/img/icons/numens.png" alt="Нумены"/>
        </p>

        {isPremiumActive ? (
          <p className="crystals-exchange-premium-note">
            <span className="crystals-exchange-premium-note-status text-coins">
              <img className="crystals-exchange-premium-note-icon" src="/img/icons/premium.png" alt="Премиум"/>
              Премиум активен до {formatPremiumExpiresAt(auth.premiumExpiresAt)}
            </span>
            <br/>
            Действует улучшенный курс обмена
            <img className="crystals-exchange-rate-icon" src="/img/icons/crystal.png" alt="Кристаллы"/>
            на
            <img className="crystals-exchange-rate-icon" src="/img/icons/numens.png" alt="Нумены"/>
          </p>
        ) : (
          <p className="crystals-exchange-premium-note">
            Премиум не активен.
            <br/>
            Улучшенный курс обмена{' '}
            <span className="text-crystals">1</span>
            <img className="crystals-exchange-rate-icon" src="/img/icons/crystal.png" alt="Кристалл"/>
            {' '}={' '}
            <span className="text-coins">{getFormattedPrice(CRYSTALS_TO_NUMENS_RATE_PREMIUM)}</span>
            <img className="crystals-exchange-rate-icon" src="/img/icons/numens.png" alt="Нумены"/>
            <br/>не действует.
          </p>
        )}

        <p className="crystals-exchange-balance">
          Доступно:
          <span className="crystals-exchange-balance-value text-crystals">
            {getFormattedPrice(auth.crystals)}
            <img className="crystals-exchange-rate-icon" src="/img/icons/crystal.png" alt="Кристаллы"/>
          </span>
        </p>

        <div className="crystals-exchange-quantity">
          <button
            type="button"
            className="crystals-exchange-quantity-step"
            onClick={() => handleQuantityStep(-1)}
            disabled={exchanging}
            aria-label="Уменьшить количество"
          >
            {MINUS_SIGN}
          </button>
          <input
            id="crystals-exchange-quantity-input"
            className="crystals-exchange-quantity-input text-crystals"
            type="number"
            min="1"
            max={MAX_QUANTITY}
            value={quantity}
            onChange={(event) => setQuantity(event.target.value)}
            disabled={exchanging}
          />
          <button
            type="button"
            className="crystals-exchange-quantity-step"
            onClick={() => handleQuantityStep(1)}
            disabled={exchanging}
            aria-label="Увеличить количество"
          >
            +
          </button>
        </div>

        <div className="crystals-exchange-result text-coins">
          {getFormattedPrice(numensResult)}
          <img className="crystals-exchange-rate-icon" src="/img/icons/numens.png" alt="Нумены"/>
        </div>

        <div className="crystals-exchange-buttons">
          <PageButton
            className="crystals-exchange-confirm-button"
            onClick={handleExchangeClick}
            pageId={PAGE_ID}
            buttonId="confirm"
            disabled={!isQuantityValid || exchanging}
          >
            {exchanging ? 'Обмен...' : (justExchanged ? 'Обменяли!' : 'Обменять')}
          </PageButton>

          <PageButton
            onClick={handleGoToCrystals}
            pageId={PAGE_ID}
            buttonId="go_to_crystals"
            style={{backgroundColor: PRIMARY_COLOR}}
          >
            За кристаллами
          </PageButton>

          {!isPremiumActive && (
            <BuyPremiumButton pageId={PAGE_ID}/>
          )}

          <PageButton
            className="main-menu-button_secondary"
            onClick={handleClose}
            pageId={PAGE_ID}
            buttonId="hide"
            disabled={exchanging}
          >
            Скрыть
          </PageButton>
        </div>
      </div>

      {notEnoughCrystalsAmount != null && (
        <NotEnoughCrystalsModal
          missingAmount={notEnoughCrystalsAmount}
          onGoToCrystals={handleGoToCrystals}
          onClose={() => setNotEnoughCrystalsAmount(null)}
        />
      )}
    </div>,
    document.body
  );
}

export default CrystalsExchangeModal;
